// Auth state + a tiny hash router.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api, clearSession, getStoredUser, getToken, setUnauthorizedHandler, storeSession } from './api';
import type { User } from './types';

interface Auth { user: User | null; login: (username: string, password: string) => Promise<void>; logout: () => void; refresh: () => Promise<void>; is: (r: string) => boolean; isAdmin: boolean; canRegister: boolean }
const AuthCtx = createContext<Auth>(null as any);
export const useAuth = () => useContext(AuthCtx);

/** Password typed at login, kept in memory only until a forced password change is done (imported accounts). */
export let loginPassword: string | null = null;
export function forgetLoginPassword() { loginPassword = null; }

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => (getToken() ? getStoredUser() : null));
  const logout = useCallback(() => { clearSession(); setUser(null); }, []);
  useEffect(() => { setUnauthorizedHandler(logout); }, [logout]);
  // the token is renewed every 10 minutes (and at start): delegations that start, end or are revoked take effect
  useEffect(() => {
    const renew = () => { if (getToken()) api.post<{ token: string; user: User }>('/api/auth/refresh').then(r => { storeSession(r.token, r.user); setUser(r.user); }).catch(() => {}); };
    renew();
    const t = setInterval(renew, 10 * 60 * 1000);
    window.addEventListener('docflow:session', renew);
    return () => { clearInterval(t); window.removeEventListener('docflow:session', renew); };
  }, []);
  const login = async (username: string, password: string) => {
    const r = await api.post<{ token: string; user: User }>('/api/auth/login', { username, password });
    storeSession(r.token, r.user);
    loginPassword = r.user.mustChangePassword ? password : null;
    setUser(r.user);
  };
  const refresh = async () => { const r = await api.post<{ token: string; user: User }>('/api/auth/refresh'); storeSession(r.token, r.user); setUser(r.user); };
  const is = (r: string) => !!user?.roles.includes(r as any);
  return <AuthCtx.Provider value={{ user, login, logout, refresh, is, isAdmin: is('ADMIN'), canRegister: is('ADMIN') || is('REGISTRY') }}>{children}</AuthCtx.Provider>;
}

export interface Route { path: string; parts: string[]; params: URLSearchParams }
function parse(): Route {
  const h = location.hash.replace(/^#/, '') || '/';
  const [p, q] = h.split('?');
  const parts = p.split('/').filter(Boolean);
  return { path: p, parts, params: new URLSearchParams(q || '') };
}
export function useRoute(): Route {
  const [r, setR] = useState(parse);
  useEffect(() => { const on = () => setR(parse()); window.addEventListener('hashchange', on); return () => window.removeEventListener('hashchange', on); }, []);
  return r;
}
export function navigate(path: string) { location.hash = path; }
